/**
 * What a reminder pop-up offers, and what pressing it means (Phase 125).
 *
 * Pure, because every one of these is a small decision with an edge: a meeting that has already
 * started, a new time that keeps the meeting's own length, a guest who may postpone their alert but
 * may not move the meeting. Each is the kind of thing that is wrong once and then wrong in somebody
 * else's calendar.
 */

/** How long a "adiar" lasts. Short, because a snooze is about the next few minutes. */
export const SNOOZE_CHOICES = [5, 10, 30] as const;

export type Move = 'PLUS_30' | 'PLUS_60' | 'TOMORROW';

const MINUTE_MS = 60_000;

/** Whole minutes from now until it starts; negative once it has begun. */
export function minutesUntil(startsAt: string, now = new Date()): number {
  return Math.round((new Date(startsAt).getTime() - now.getTime()) / MINUTE_MS);
}

/**
 * The new times for a quick reschedule.
 *
 * **The meeting keeps its length.** Moving the start and leaving the end where it was is how a
 * half-hour call becomes a two-minute one, and nobody notices until they are in it. "Amanhã" keeps
 * the time of day as well, which is what somebody means by it.
 */
export function moveTo(
  startsAt: string,
  endsAt: string,
  move: Move,
): { startsAt: string; endsAt: string } {
  const start = new Date(startsAt);
  const duration = new Date(endsAt).getTime() - start.getTime();

  const next =
    move === 'TOMORROW'
      ? new Date(
          start.getFullYear(),
          start.getMonth(),
          start.getDate() + 1,
          start.getHours(),
          start.getMinutes(),
          0,
          0,
        )
      : new Date(start.getTime() + (move === 'PLUS_30' ? 30 : 60) * MINUTE_MS);

  return {
    startsAt: next.toISOString(),
    endsAt: new Date(next.getTime() + duration).toISOString(),
  };
}

export interface Viewer {
  userId: string | null;
}

export interface RemindedEvent {
  organizerId: string | null;
  ownerId: string | null;
  guestsCanModify: boolean;
}

/**
 * Whether this person may move or call off the meeting.
 *
 * Offered only to somebody who can actually do it. The API refuses the rest — a guest who can move
 * the meeting is not a guest — and a button that exists to produce a refusal is worse than no
 * button: the person reads it as the product being broken rather than as the rule it is.
 */
export function canOrganise(event: RemindedEvent, viewer: Viewer): boolean {
  if (!viewer.userId) return false;

  return (
    event.organizerId === viewer.userId || event.ownerId === viewer.userId || event.guestsCanModify
  );
}
